/**
 * Local Storage Service
 * 
 * Provides offline persistence for todos and calendar events.
 * Uses localStorage for now, will sync to D1 later.
 */

import type { Todo } from '../../types/todo.js';
import type { CalendarEvent } from '../../types/calendar.js';

const STORAGE_KEYS = {
  TODOS: 'pivot_todos',
  CALENDAR_EVENTS: 'pivot_calendar_events'
};

/**
 * Todo Storage
 */
export class TodoStorage {
  /**
   * Get all todos from localStorage
   */
  static getAll(): Todo[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.TODOS);
      if (!data) return [];
      return JSON.parse(data) as Todo[];
    } catch (error) {
      console.error('[TodoStorage] Error loading todos:', error);
      return [];
    }
  }

  /**
   * Save todos to localStorage
   */
  static save(todos: Todo[]): void {
    try {
      localStorage.setItem(STORAGE_KEYS.TODOS, JSON.stringify(todos));
    } catch (error) {
      console.error('[TodoStorage] Error saving todos:', error);
    }
  }

  /**
   * Add a new todo
   */
  static add(todo: Todo): void {
    const todos = this.getAll();
    todos.push(todo);
    this.save(todos);
  }

  /**
   * Update a todo
   */
  static update(todoId: string, updates: Partial<Todo>): void {
    const todos = this.getAll();
    const index = todos.findIndex(t => t.id === todoId);
    if (index !== -1) {
      todos[index] = { ...todos[index], ...updates, updated_at: Date.now() };
      this.save(todos);
    }
  }

  /**
   * Delete a todo
   */
  static delete(todoId: string): void {
    const todos = this.getAll();
    const filtered = todos.filter(t => t.id !== todoId);
    this.save(filtered);
  }
}

/**
 * Calendar Event Storage
 */
export class CalendarEventStorage {
  /**
   * Get all calendar events from localStorage
   */
  static getAll(): CalendarEvent[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CALENDAR_EVENTS);
      if (!data) return [];
      return JSON.parse(data) as CalendarEvent[];
    } catch (error) {
      console.error('[CalendarEventStorage] Error loading events:', error);
      return [];
    }
  }

  /**
   * Save calendar events to localStorage
   */
  static save(events: CalendarEvent[]): void {
    try {
      localStorage.setItem(STORAGE_KEYS.CALENDAR_EVENTS, JSON.stringify(events));
    } catch (error) {
      console.error('[CalendarEventStorage] Error saving events:', error);
    }
  }

  /**
   * Add a new calendar event
   */
  static add(event: CalendarEvent): void {
    const events = this.getAll();
    events.push(event);
    this.save(events);
  }

  /**
   * Update a calendar event
   */
  static update(eventId: string, updates: Partial<CalendarEvent>): void {
    const events = this.getAll();
    const index = events.findIndex(e => e.id === eventId);
    if (index !== -1) {
      events[index] = { ...events[index], ...updates, updated_at: Date.now() };
      this.save(events);
    }
  }

  /**
   * Delete a calendar event
   */
  static delete(eventId: string): void {
    const events = this.getAll();
    const filtered = events.filter(e => e.id !== eventId);
    this.save(filtered);
  }
}

